import React from 'react';

const BenchmarkToolbar = ({
    chartView,
    setChartView,
    cumulativeSubMode,
    setCumulativeSubMode,
    multiGridCount,
    setMultiGridCount,
    setIsMultiGridFullscreen,
    showWholeBaseline,
    setShowWholeBaseline,
    showLiveGridFiltered,
    setShowLiveGridFiltered,
    baselineScope,
    setBaselineScope,
    filterOnBaseline,
    setFilterOnBaseline,
    highlightLive,
    setHighlightLive,
    pnlUnit,
    setPnlUnit,
    groupingMode,
    setGroupingMode,
    selectedFSR,
    setSelectedFSR,
    selectedMABits,
    setSelectedMABits,
    selectedCEPE,
    setSelectedCEPE,
    equityChartData,
    selectedSeries,
    multiPanels,
    setMultiPanels,
    pinnedSeries,
    setPinnedSeries
}) => {
    return (
        <>
            {/* Header Title Row */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-800/80 shrink-0">
                <div className="flex items-center gap-3">
                    <div>
                        <h2 className="text-sm font-extrabold text-white tracking-wide">
                            Cumulative Benchmark Analysis
                        </h2>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                            {chartView === 'cumulative'
                                ? 'Whole Tradelist Baseline (Cyan) vs. Live Regime Grid Filtered (Emerald)'
                                : 'Chronological daily aligned backtest performance'}
                        </p>
                    </div>
                    {pinnedSeries && (
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] font-bold shadow-sm">
                            <span>📌 Pinned:</span>
                            <span className="font-mono max-w-[160px] truncate" title={pinnedSeries}>{pinnedSeries}</span>
                            <button
                                onClick={() => setPinnedSeries(null)}
                                className="ml-1 text-gray-400 hover:text-white text-xs font-bold transition-colors cursor-pointer"
                                title="Click to Unpin View"
                            >
                                ✕
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2.5 py-1">
                    {/* Sub-Mode Switcher for Cumulative View */}
                    {chartView === 'cumulative' && (
                        <div className="flex items-center gap-1 bg-[#14161f] p-0.5 rounded border border-gray-700 mr-2">
                            <button
                                onClick={() => {
                                    setCumulativeSubMode('benchmark');
                                    setShowWholeBaseline(true);
                                }}
                                className={`px-2 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                    cumulativeSubMode === 'benchmark' ? 'bg-emerald-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                                }`}
                                title="Overlay Whole Tradelist (Baseline) vs Live Grid Filtered line"
                            >
                                Benchmark Overlay
                            </button>
                            <button
                                onClick={() => {
                                    setCumulativeSubMode('multi');
                                    const list = equityChartData.seriesList || [];
                                    const checkedSeries = Object.keys(selectedSeries).filter(k => selectedSeries[k]);
                                    const firstSel = checkedSeries.length > 0 ? checkedSeries[0] : (list[0] || 'ALL');
                                    const initList = [firstSel];
                                    list.forEach(s => { if (!initList.includes(s)) initList.push(s); });
                                    setMultiPanels([0, 1, 2, 3, 4, 5].map(i => ({
                                        id: i,
                                        strategy: initList[i] || 'ALL',
                                        selectedFSR: ['R', 'S', 'F'],
                                        selectedMABits: ['00', '01', '10', '11'],
                                        selectedCEPE: ['CE', 'PE']
                                    })));
                                }}
                                className={`px-2 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                    cumulativeSubMode === 'multi' ? 'bg-purple-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                                }`}
                                title="Side-by-Side Multi-Chart Comparison Grid (2, 4, or 6 Charts)"
                            >
                                Multi-Grid ({multiGridCount})
                            </button>
                        </div>
                    )}

                    {/* Multi-Grid Panel Count & Scope Controls */}
                    {chartView === 'cumulative' && cumulativeSubMode === 'multi' && (
                        <div className="flex flex-wrap items-center gap-2 mr-2">
                            <div className="flex items-center gap-1 bg-[#14161f] p-0.5 rounded border border-gray-700">
                                <span className="text-[9px] text-gray-400 font-bold uppercase px-1">Grid Panels:</span>
                                {[2, 4, 6].map(count => (
                                    <button
                                        key={count}
                                        onClick={() => setMultiGridCount(count)}
                                        className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                            multiGridCount === count ? 'bg-purple-600 text-white shadow' : 'text-gray-400 hover:text-gray-200'
                                        }`}
                                    >
                                        {count} Charts
                                    </button>
                                ))}
                            </div>

                            {/* Baseline Scope Toggle */}
                            <button
                                onClick={() => setBaselineScope(prev => prev === 'all' ? 'selected' : 'all')}
                                className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all border cursor-pointer ${
                                    baselineScope === 'all'
                                        ? 'bg-purple-600/40 border-purple-400 text-purple-200 font-semibold shadow'
                                        : 'bg-gray-800/80 border-gray-700 text-gray-300 hover:text-white'
                                }`}
                                title="Toggle Cyan Line Baseline: Compare against All Strategies Global Portfolio (ON) vs Selected Panel Strategy Only (OFF)"
                            >
                                {baselineScope === 'all' ? '🌐 Baseline: All Strategies' : '🎯 Baseline: Selected Strategy'}
                            </button>

                            <button
                                onClick={() => setIsMultiGridFullscreen(true)}
                                className="px-3 py-1 rounded text-xs font-extrabold bg-purple-600 hover:bg-purple-500 text-white transition-all flex items-center gap-1.5 shadow-md border border-purple-400/50 cursor-pointer hover:shadow-purple-500/20"
                                title="Open Multi-Grid Workspace in Fullscreen Window"
                            >
                                <span>⛶</span> Fullscreen Grid Mode
                            </button>
                        </div>
                    )}

                    {/* Benchmark Line Visibility Toggles */}
                    {chartView === 'cumulative' && cumulativeSubMode === 'benchmark' && (
                        <div className="flex items-center gap-1 bg-[#14161f] p-0.5 rounded border border-gray-700 mr-2">
                            <button
                                onClick={() => setShowWholeBaseline(prev => !prev)}
                                className={`px-2 py-1 rounded text-[10px] font-bold transition-all border cursor-pointer ${
                                    showWholeBaseline
                                        ? 'bg-white/20 border-white/60 text-white font-semibold'
                                        : 'bg-transparent border-transparent text-gray-500 hover:text-gray-300'
                                }`}
                                title="Toggle Baseline Whole Tradelist Line"
                            >
                                {showWholeBaseline ? '✓ Baseline (Whole)' : 'Baseline (Whole)'}
                            </button>
                            <button
                                onClick={() => setShowLiveGridFiltered(prev => !prev)}
                                className={`px-2 py-1 rounded text-[10px] font-bold transition-all border cursor-pointer ${
                                    showLiveGridFiltered
                                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300 font-semibold'
                                        : 'bg-transparent border-transparent text-gray-500 hover:text-gray-300'
                                }`}
                                title="Toggle Live Grid Filtered Line"
                            >
                                {showLiveGridFiltered ? '✓ Live Grid Filtered' : 'Live Grid Filtered'}
                            </button>
                            <button
                                onClick={() => setBaselineScope(prev => prev === 'all' ? 'selected' : 'all')}
                                className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all border cursor-pointer ${
                                    baselineScope === 'all'
                                        ? 'bg-purple-600/40 border-purple-400 text-purple-200 font-semibold shadow'
                                        : 'bg-gray-800/80 border-gray-700 text-gray-300 hover:text-white'
                                }`}
                                title="Toggle Baseline Scope: All Strategies Global Benchmark (ON) vs Selected Series Only (OFF)"
                            >
                                {baselineScope === 'all' ? '🌐 Scope: All Strategies' : '🎯 Scope: Selected Series'}
                            </button>
                            <button
                                onClick={() => setFilterOnBaseline && setFilterOnBaseline(prev => !prev)}
                                className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all border cursor-pointer ${
                                    filterOnBaseline
                                        ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 font-semibold shadow'
                                        : 'bg-gray-800/80 border-gray-700 text-gray-400 hover:text-white'
                                }`}
                                title="Toggle filtering on Baseline: OFF = Raw Unfiltered Baseline, ON = Apply FSR/MA/CE-PE filters to Baseline"
                            >
                                {filterOnBaseline ? 'Filter Baseline: ON' : 'Filter Baseline: OFF'}
                            </button>
                        </div>
                    )}

                    {/* View Switcher (Cumulative vs Daily PnL) */}
                    <span className="text-[10px] text-gray-400 font-bold uppercase">View:</span>
                    <div className="flex bg-[#14161f] p-0.5 rounded border border-gray-700 gap-0.5 mr-2">
                        <button
                            onClick={() => setChartView('cumulative')}
                            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                chartView === 'cumulative' ? 'bg-blue-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            Cumulative
                        </button>
                        <button
                            onClick={() => setChartView('daily')}
                            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                chartView === 'daily' ? 'bg-blue-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            Daily PnL
                        </button>
                    </div>

                    {/* Highlight Live Toggle */}
                    <button
                        onClick={() => setHighlightLive(prev => !prev)}
                        className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all mr-2 cursor-pointer ${
                            highlightLive
                                ? 'bg-emerald-600 text-white shadow font-semibold'
                                : 'bg-[#14161f] text-gray-400 border border-gray-700 hover:text-gray-200'
                        }`}
                        title="Toggle Highlighting: Live series become bold & solid, non-live series become faded & dashed"
                    >
                        {highlightLive ? '✓ Highlighting Live' : 'Highlight Live'}
                    </button>

                    {/* Unit Switcher */}
                    <span className="text-[10px] text-gray-400 font-bold uppercase">Unit:</span>
                    <div className="flex bg-[#14161f] p-0.5 rounded border border-gray-700 gap-0.5 mr-2">
                        <button
                            onClick={() => setPnlUnit('inr')}
                            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                pnlUnit === 'inr' ? 'bg-blue-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            ₹ INR
                        </button>
                        <button
                            onClick={() => setPnlUnit('pct')}
                            className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all cursor-pointer ${
                                pnlUnit === 'pct' ? 'bg-blue-600 text-white shadow font-semibold' : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            % Percent
                        </button>
                    </div>

                    <span className="text-[10px] text-gray-400 font-bold uppercase">Grouping:</span>
                    <select
                        value={groupingMode}
                        onChange={(e) => setGroupingMode(e.target.value)}
                        className="bg-[#14161f] border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 outline-none focus:border-blue-500 cursor-pointer font-bold"
                    >
                        <option value="strategy">Strategy-wise</option>
                        <option value="regime">Regime-wise</option>
                        <option value="asset">Asset-wise</option>
                        <option value="asset_strategy">Asset + Strategy</option>
                        <option value="asset_regime">Asset + Regime</option>
                        <option value="asset_strategy_regime">Asset + Strategy + Regime</option>
                        <option value="portfolio">Single Portfolio (All Combined)</option>
                    </select>
                </div>
            </div>

            {/* Granular Regime Filter Toolbar for Cumulative View */}
            {chartView === 'cumulative' && (
                <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-3 border-b border-gray-800/60 shrink-0 text-[10px]">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-400 uppercase">FSR VIX Regime:</span>
                        <div className="flex gap-1">
                            {[
                                { key: 'R', label: 'Rising VIX (R)', color: 'text-amber-400' },
                                { key: 'S', label: 'Flat VIX (S)', color: 'text-blue-400' },
                                { key: 'F', label: 'Falling VIX (F)', color: 'text-emerald-400' },
                            ].map(({ key, label, color }) => {
                                const isSelected = selectedFSR.includes(key);
                                return (
                                    <button
                                        key={key}
                                        onClick={() => {
                                            setSelectedFSR(prev => isSelected ? prev.filter(k => k !== key) : [...prev, key]);
                                        }}
                                        className={`px-2 py-0.5 rounded font-bold transition-all border cursor-pointer ${
                                            isSelected
                                                ? 'bg-gray-800 border-gray-600 ' + color
                                                : 'bg-[#14161f] border-gray-800 text-gray-600 hover:text-gray-400'
                                        }`}
                                    >
                                        {isSelected ? '✓ ' : ''}{label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-400 uppercase">MA Bits (MA10/MA20):</span>
                        <div className="flex gap-1">
                            {['00', '01', '10', '11'].map(bit => {
                                const isSelected = selectedMABits.includes(bit);
                                return (
                                    <button
                                        key={bit}
                                        onClick={() => {
                                            setSelectedMABits(prev => isSelected ? prev.filter(b => b !== bit) : [...prev, bit]);
                                        }}
                                        className={`px-2 py-0.5 rounded font-mono font-bold transition-all border cursor-pointer ${
                                            isSelected
                                                ? 'bg-blue-600/20 border-blue-500 text-blue-300'
                                                : 'bg-[#14161f] border-gray-800 text-gray-600 hover:text-gray-400'
                                        }`}
                                    >
                                        {bit}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-400 uppercase">Option Type:</span>
                        <div className="flex gap-1">
                            {['CE', 'PE'].map(type => {
                                const isSelected = selectedCEPE.includes(type);
                                return (
                                    <button
                                        key={type}
                                        onClick={() => {
                                            setSelectedCEPE(prev => isSelected ? prev.filter(t => t !== type) : [...prev, type]);
                                        }}
                                        className={`px-2 py-0.5 rounded font-bold transition-all border cursor-pointer ${
                                            isSelected
                                                ? 'bg-purple-600/20 border-purple-500 text-purple-300'
                                                : 'bg-[#14161f] border-gray-800 text-gray-600 hover:text-gray-400'
                                        }`}
                                    >
                                        {isSelected ? '✓ ' : ''}{type}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BenchmarkToolbar;
